import { initializeApp } from "firebase/app";
import {
  getAuth,
  sendPasswordResetEmail,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  getRedirectResult,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyDFmrnc6rYeeW7J4NJGbDvz47t23yZzXAo",
  authDomain: "windpuls-auth.firebaseapp.com",
  databaseURL:
    "https://windpuls-auth-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "windpuls-auth",
  storageBucket: "windpuls-auth.appspot.com",
  messagingSenderId: "819058730095",
  appId: "1:819058730095:web:802dd9966473bb7092599a",
  measurementId: "G-6W1B7M3BHX",
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export default app;

export async function currentUserIsAdmin() {
  // Force token refresh
  await auth.currentUser?.getIdToken();
  const idTokenResult = await auth.currentUser?.getIdTokenResult();
  if (
    idTokenResult &&
    idTokenResult.claims &&
    idTokenResult.claims.role?.toLowerCase() === "admin"
  ) {
    return true;
  }
  return false;
}

export const passwordReset = async (email: string) => {
  await sendPasswordResetEmail(auth, email);
};

export const confirmThePasswordReset = async (
  code: string,
  newPassword: string
) => {
  if (!code && !newPassword) return;
  return await confirmPasswordReset(auth, code, newPassword);
};

export const signUp = async (email: string, password: string) => {
  if (!email && !password) return;
  return await createUserWithEmailAndPassword(auth, email, password);
};

export const updateName = async (firstname: string, lastname: string) => {
  if (!firstname || !lastname) return;
  const currentUser = auth.currentUser;
  if (!currentUser) return;
  return await updateProfile(currentUser, {
    displayName: firstname + " " + lastname,
  });
};

export const updateDisplayName = async (displayName: string) => {
  if (!displayName) return;
  const currentUser = auth.currentUser;
  if (!currentUser) return;
  return await updateProfile(currentUser, {
    displayName: displayName,
  });
};

export const getCurrentUserProfile = () => {
  const currentUser = auth.currentUser;
  if (!currentUser) return null;

  return {
    displayName: currentUser.displayName,
    email: currentUser.email,
    photoURL: currentUser.photoURL,
    uid: currentUser.uid,
    emailVerified: currentUser.emailVerified,
  };
};

export const sendPasswordResetEmailToCurrentUser = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser || !currentUser.email) {
    throw new Error("No authenticated user found");
  }
  return await sendPasswordResetEmail(auth, currentUser.email);
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out:", error);
    throw error;
  }
};

// Google OAuth
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    // Request basic profile info
    provider.addScope('email');
    provider.addScope('profile');

    // Use popup for better UX
    const result = await signInWithPopup(auth, provider);

    // Get user info
    const user = result.user;
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential?.accessToken;

    return {
      user,
      accessToken,
      provider: 'google'
    };
  } catch (error: any) {
    console.error('Google sign-in error:', error);
    throw handleAuthError(error);
  }
};

// Apple OAuth
export const signInWithApple = async () => {
  try {
    const provider = new OAuthProvider('apple.com');
    // Request basic profile info
    provider.addScope('email');
    provider.addScope('name');

    // Use popup for better UX
    const result = await signInWithPopup(auth, provider);

    // Get user info
    const user = result.user;
    const credential = OAuthProvider.credentialFromResult(result);
    const accessToken = credential?.accessToken;

    return {
      user,
      accessToken,
      provider: 'apple'
    };
  } catch (error: any) {
    console.error('Apple sign-in error:', error);
    throw handleAuthError(error);
  }
};

// Handle OAuth redirect result (for mobile/browsers that block popups)
export const handleOAuthRedirect = async () => {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      const user = result.user;
      // Determine which provider was used
      let provider = 'unknown';

      // Check the user's provider data
      if (user.providerData && user.providerData.length > 0) {
        const providerId = user.providerData[0].providerId;
        if (providerId === 'google.com') {
          provider = 'google';
        } else if (providerId === 'apple.com') {
          provider = 'apple';
        }
      }

      return { user, provider };
    }
    return null;
  } catch (error: any) {
    console.error('OAuth redirect error:', error);
    throw handleAuthError(error);
  }
};

// Helper function to handle auth errors
const handleAuthError = (error: any) => {
  if (error.code === 'auth/popup-blocked') {
    return new Error('Popup was blocked. Please allow popups for this site and try again.');
  } else if (error.code === 'auth/popup-closed-by-user') {
    return new Error('Sign-in was cancelled.');
  } else if (error.code === 'auth/cancelled-popup-request') {
    return new Error('Sign-in was cancelled.');
  } else if (error.code === 'auth/account-exists-with-different-credential') {
    return new Error('An account already exists with the same email address but different sign-in credentials. Try signing in with the other method.');
  } else if (error.code === 'auth/email-already-in-use') {
    return new Error('This email is already in use by another account.');
  } else {
    return new Error(error.message || 'An error occurred during sign-in.');
  }
};
